import { Button } from "@/components/ui/button";
import type { OnboardingStep } from "@/features/onboarding/types";
import { useTranslation } from "@/features/utils/i18n";
import { useTitle } from "@/features/utils/use-title";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { AgentBubble, BlockBubble, OnboardingChatShell } from "./chat";

/**
 * One step, as a turn in the conversation.
 *
 * The step's title is the agent's question; its fields are the answer the
 * client is composing. Everything the frame used to carry — progress, the way
 * out ("Terminer plus tard") — has moved into the chat header, which is where
 * a conversation keeps its context.
 */
export function OnboardingLayout({
  step,
  title,
  intro,
  children,
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  isSubmitting,
  analysisPending,
  footer,
}: {
  step: OnboardingStep;
  title: string;
  /**
   * What the agent says before it asks.
   *
   * The question alone ("Your phone line") is a form label with a speech
   * bubble around it — it tells the client what to fill in and never why.
   * This is the sentence that carries the why, and it is a separate bubble
   * because that is how someone talks: context, then the question. It is not
   * replayed in the transcript, so the history stays short.
   */
  intro?: string;
  children: ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  isSubmitting?: boolean;
  /**
   * The website analysis is still running behind this screen.
   *
   * Without this the work is completely invisible: the client hands over a URL,
   * lands on an unrelated question, and only finds out anything happened three
   * screens later. A line in the thread — where the agent would say it — keeps
   * the wait legible while it is being used.
   */
  analysisPending?: boolean;
  /** Replaces the default next/back bar entirely (used by the first screen). */
  footer?: ReactNode;
}) {
  const { t } = useTranslation("onboarding");
  useTitle(title);

  return (
    <OnboardingChatShell step={step}>
      {analysisPending && <AgentBubble>{t("progress.analysing")}</AgentBubble>}
      {intro && <AgentBubble>{intro}</AgentBubble>}
      <AgentBubble>{title}</AgentBubble>

      <BlockBubble>
        <div className="space-y-5">
          {children}

          {footer ?? (
            <div className="flex items-center gap-3">
              {onBack && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onBack}
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  {t("actions.back")}
                </Button>
              )}
              <div className="flex-1" />
              {onNext && (
                <Button
                  type="button"
                  onClick={onNext}
                  disabled={nextDisabled || isSubmitting}
                >
                  {isSubmitting
                    ? t("actions.saving")
                    : (nextLabel ?? t("actions.next"))}
                </Button>
              )}
            </div>
          )}
        </div>
      </BlockBubble>
    </OnboardingChatShell>
  );
}
